import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Save, FileText, Palette, Type, Image as ImageIcon } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
interface DeliveryNoteSettings {
  companyName: string;
  companySubtitle: string;
  showLogo: boolean;
  showOrderDateTime: boolean;
  showDeliveryDateTime: boolean;
  showDeliveryType: boolean;
  showEmployeeCode: boolean;
  showRequests: boolean;
  showQuantity: boolean;
  showSignature: boolean;
  showNotes: boolean;
  notesLabel: string;
  showFooter: boolean;
  footerText: string;
  // Aesthetic settings
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  fontSize: string;
  headerStyle: 'modern' | 'classic' | 'minimal' | 'bold';
  tableStyle: 'striped' | 'bordered' | 'minimal' | 'compact';
  borderRadius: string;
  logoUrl: string;
  logoSize: string;
}
export function DeliveryNoteSettings() {
  const {
    showToast
  } = useToast();
  const [settings, setSettings] = useState<DeliveryNoteSettings>(() => {
    const saved = localStorage.getItem('delivery_note_settings');
    return saved ? JSON.parse(saved) : {
      companyName: 'La Cochera',
      companySubtitle: 'Restaurant',
      showLogo: true,
      showOrderDateTime: false,
      showDeliveryDateTime: false,
      showDeliveryType: false,
      showEmployeeCode: true,
      showRequests: false,
      showQuantity: true,
      showSignature: true,
      showNotes: true,
      notesLabel: 'Notas',
      showFooter: true,
      footerText: 'Documento generado por La Cochera Restaurant',
      // Aesthetic defaults
      primaryColor: '#000000',
      accentColor: '#3b82f6',
      fontFamily: 'Segoe UI',
      fontSize: '11pt',
      headerStyle: 'modern',
      tableStyle: 'striped',
      borderRadius: '8px',
      logoUrl: '',
      logoSize: '60px'
    };
  });
  const handleSave = () => {
    localStorage.setItem('delivery_note_settings', JSON.stringify(settings));
    showToast('success', 'Configuración guardada', 'Los cambios se aplicarán en el próximo conduce');
  };
  const fontOptions = [{
    value: 'Segoe UI',
    label: 'Segoe UI (Moderno)'
  }, {
    value: 'Arial',
    label: 'Arial (Clásico)'
  }, {
    value: 'Times New Roman',
    label: 'Times New Roman (Formal)'
  }, {
    value: 'Georgia',
    label: 'Georgia (Elegante)'
  }, {
    value: 'Courier New',
    label: 'Courier New (Monoespaciado)'
  }, {
    value: 'Verdana',
    label: 'Verdana (Legible)'
  }];
  const fontSizeOptions = [{
    value: '9pt',
    label: 'Pequeño (9pt)'
  }, {
    value: '10pt',
    label: 'Normal (10pt)'
  }, {
    value: '11pt',
    label: 'Mediano (11pt)'
  }, {
    value: '12pt',
    label: 'Grande (12pt)'
  }];
  const headerStyleOptions = [{
    value: 'modern',
    label: 'Moderno',
    description: 'Limpio y minimalista'
  }, {
    value: 'classic',
    label: 'Clásico',
    description: 'Tradicional y formal'
  }, {
    value: 'minimal',
    label: 'Minimal',
    description: 'Ultra simple'
  }, {
    value: 'bold',
    label: 'Bold',
    description: 'Impactante y destacado'
  }];
  const tableStyleOptions = [{
    value: 'striped',
    label: 'Rayado',
    description: 'Filas alternadas'
  }, {
    value: 'bordered',
    label: 'Con Bordes',
    description: 'Bordes completos'
  }, {
    value: 'minimal',
    label: 'Minimal',
    description: 'Solo líneas esenciales'
  }, {
    value: 'compact',
    label: 'Compacto',
    description: 'Espaciado reducido'
  }];
  return <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-id="element-2447">
      <div className="mb-8" data-id="element-2448">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2449">
          Configuración del Conduce
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-2450">
          Personaliza el formato, contenido y estética del conduce de entrega
        </p>
      </div>

      <div className="space-y-6" data-id="element-2451">
        {/* Company Information */}
        <Card data-id="element-2452">
          <div className="flex items-center gap-3 mb-6" data-id="element-2453">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg" data-id="element-2454">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2455" />
            </div>
            <div data-id="element-2456">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2457">
                Información de la Empresa
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2458">
                Datos que aparecen en el encabezado
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-2459">
            <div data-id="element-2460">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2461">
                Nombre de la Empresa
              </label>
              <input type="text" value={settings.companyName} onChange={e => setSettings({
              ...settings,
              companyName: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="La Cochera" data-id="element-2462" />
            </div>

            <div data-id="element-2463">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2464">
                Subtítulo
              </label>
              <input type="text" value={settings.companySubtitle} onChange={e => setSettings({
              ...settings,
              companySubtitle: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Restaurant" data-id="element-2465" />
            </div>

            <div data-id="element-2466">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2467">
                Logo (URL de imagen)
              </label>
              <input type="text" value={settings.logoUrl} onChange={e => setSettings({
              ...settings,
              logoUrl: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="https://ejemplo.com/logo.png" data-id="element-2468" />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2469">
                Deja vacío para mostrar solo texto
              </p>
            </div>

            {settings.logoUrl && <div data-id="element-2470">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2471">
                  Tamaño del Logo
                </label>
                <select value={settings.logoSize} onChange={e => setSettings({
              ...settings,
              logoSize: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2472">
                  <option value="40px" data-id="element-2473">Pequeño (40px)</option>
                  <option value="50px" data-id="element-2474">Mediano (50px)</option>
                  <option value="60px" data-id="element-2475">Grande (60px)</option>
                  <option value="80px" data-id="element-2476">Extra Grande (80px)</option>
                </select>
              </div>}
          </div>
        </Card>

        {/* Aesthetic Settings - Colors */}
        <Card data-id="element-2477">
          <div className="flex items-center gap-3 mb-6" data-id="element-2478">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg" data-id="element-2479">
              <Palette className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-2480" />
            </div>
            <div data-id="element-2481">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2482">
                Colores y Estilo Visual
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2483">
                Personaliza la paleta de colores del documento
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2484">
            <div data-id="element-2485">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2486">
                Color Principal
              </label>
              <div className="flex gap-3 items-center" data-id="element-2487">
                <input type="color" value={settings.primaryColor} onChange={e => setSettings({
                ...settings,
                primaryColor: e.target.value
              })} className="w-16 h-10 rounded-lg border border-gray-300 dark:border-gray-700 cursor-pointer" data-id="element-2488" />
                <input type="text" value={settings.primaryColor} onChange={e => setSettings({
                ...settings,
                primaryColor: e.target.value
              })} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm" placeholder="#000000" data-id="element-2489" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2490">
                Usado en encabezados y bordes principales
              </p>
            </div>

            <div data-id="element-2491">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2492">
                Color de Acento
              </label>
              <div className="flex gap-3 items-center" data-id="element-2493">
                <input type="color" value={settings.accentColor} onChange={e => setSettings({
                ...settings,
                accentColor: e.target.value
              })} className="w-16 h-10 rounded-lg border border-gray-300 dark:border-gray-700 cursor-pointer" data-id="element-2494" />
                <input type="text" value={settings.accentColor} onChange={e => setSettings({
                ...settings,
                accentColor: e.target.value
              })} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm" placeholder="#3b82f6" data-id="element-2495" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-2496">
                Usado en detalles y elementos destacados
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2497">
            <div data-id="element-2498">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2499">
                Estilo del Encabezado
              </label>
              <div className="space-y-2" data-id="element-2500">
                {headerStyleOptions.map(option => <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2501">
                    <input type="radio" name="headerStyle" value={option.value} checked={settings.headerStyle === option.value} onChange={e => setSettings({
                  ...settings,
                  headerStyle: e.target.value as any
                })} className="w-4 h-4 text-black focus:ring-black dark:text-white dark:focus:ring-white" data-id="element-2502" />
                    <div data-id="element-2503">
                      <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2504">
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2505">
                        {option.description}
                      </p>
                    </div>
                  </label>)}
              </div>
            </div>

            <div data-id="element-2506">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2507">
                Estilo de la Tabla
              </label>
              <div className="space-y-2" data-id="element-2508">
                {tableStyleOptions.map(option => <label key={option.value} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" data-id="element-2509">
                    <input type="radio" name="tableStyle" value={option.value} checked={settings.tableStyle === option.value} onChange={e => setSettings({
                  ...settings,
                  tableStyle: e.target.value as any
                })} className="w-4 h-4 text-black focus:ring-black dark:text-white dark:focus:ring-white" data-id="element-2510" />
                    <div data-id="element-2511">
                      <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2512">
                        {option.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2513">
                        {option.description}
                      </p>
                    </div>
                  </label>)}
              </div>
            </div>
          </div>

          <div className="mt-6" data-id="element-2514">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2515">
              Redondeo de Bordes
            </label>
            <select value={settings.borderRadius} onChange={e => setSettings({
            ...settings,
            borderRadius: e.target.value
          })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2516">
              <option value="0px" data-id="element-2517">Sin redondeo (Cuadrado)</option>
              <option value="4px" data-id="element-2518">Ligero (4px)</option>
              <option value="8px" data-id="element-2519">Moderado (8px)</option>
              <option value="12px" data-id="element-2520">Redondeado (12px)</option>
            </select>
          </div>
        </Card>

        {/* Typography Settings */}
        <Card data-id="element-2521">
          <div className="flex items-center gap-3 mb-6" data-id="element-2522">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg" data-id="element-2523">
              <Type className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-2524" />
            </div>
            <div data-id="element-2525">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2526">
                Tipografía
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2527">
                Personaliza la fuente y tamaño del texto
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2528">
            <div data-id="element-2529">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2530">
                Familia de Fuente
              </label>
              <select value={settings.fontFamily} onChange={e => setSettings({
              ...settings,
              fontFamily: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2531">
                {fontOptions.map(option => <option key={option.value} value={option.value} data-id="element-2532">
                    {option.label}
                  </option>)}
              </select>
            </div>

            <div data-id="element-2533">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2534">
                Tamaño de Fuente
              </label>
              <select value={settings.fontSize} onChange={e => setSettings({
              ...settings,
              fontSize: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2535">
                {fontSizeOptions.map(option => <option key={option.value} value={option.value} data-id="element-2536">
                    {option.label}
                  </option>)}
              </select>
            </div>
          </div>
        </Card>

        {/* Table Columns */}
        <Card data-id="element-2537">
          <div className="flex items-center gap-3 mb-6" data-id="element-2538">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg" data-id="element-2539">
              <FileText className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-2540" />
            </div>
            <div data-id="element-2541">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2542">
                Columnas de la Tabla
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2543">
                Selecciona qué columnas mostrar
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2544">
            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2545">
              <div data-id="element-2546">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2547">
                  Fecha/Hora Orden
                </p>
              </div>
              <input type="checkbox" checked={settings.showOrderDateTime} onChange={e => setSettings({
              ...settings,
              showOrderDateTime: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2548" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2549">
              <div data-id="element-2550">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2551">
                  Fecha/Hora Entrega
                </p>
              </div>
              <input type="checkbox" checked={settings.showDeliveryDateTime} onChange={e => setSettings({
              ...settings,
              showDeliveryDateTime: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2552" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2553">
              <div data-id="element-2554">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2555">
                  Tipo de Entrega
                </p>
              </div>
              <input type="checkbox" checked={settings.showDeliveryType} onChange={e => setSettings({
              ...settings,
              showDeliveryType: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2556" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2557">
              <div data-id="element-2558">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2559">
                  Código Empleado
                </p>
              </div>
              <input type="checkbox" checked={settings.showEmployeeCode} onChange={e => setSettings({
              ...settings,
              showEmployeeCode: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2560" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2561">
              <div data-id="element-2562">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2563">
                  Peticiones
                </p>
              </div>
              <input type="checkbox" checked={settings.showRequests} onChange={e => setSettings({
              ...settings,
              showRequests: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2564" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2565">
              <div data-id="element-2566">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2567">
                  Cantidad
                </p>
              </div>
              <input type="checkbox" checked={settings.showQuantity} onChange={e => setSettings({
              ...settings,
              showQuantity: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2568" />
            </label>

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2569">
              <div data-id="element-2570">
                <p className="font-medium text-gray-900 dark:text-white text-sm" data-id="element-2571">
                  Firma
                </p>
              </div>
              <input type="checkbox" checked={settings.showSignature} onChange={e => setSettings({
              ...settings,
              showSignature: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2572" />
            </label>
          </div>
        </Card>

        {/* Additional Sections */}
        <Card data-id="element-2573">
          <div className="flex items-center gap-3 mb-6" data-id="element-2574">
            <div className="p-2 bg-pink-100 dark:bg-pink-900/20 rounded-lg" data-id="element-2575">
              <FileText className="w-5 h-5 text-pink-600 dark:text-pink-400" data-id="element-2576" />
            </div>
            <div data-id="element-2577">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2578">
                Secciones Adicionales
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2579">
                Elementos extra del conduce
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-2580">
            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2581">
              <div data-id="element-2582">
                <p className="font-medium text-gray-900 dark:text-white" data-id="element-2583">
                  Sección de Notas
                </p>
              </div>
              <input type="checkbox" checked={settings.showNotes} onChange={e => setSettings({
              ...settings,
              showNotes: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2584" />
            </label>

            {settings.showNotes && <div className="ml-4" data-id="element-2585">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2586">
                  Etiqueta de Notas
                </label>
                <input type="text" value={settings.notesLabel} onChange={e => setSettings({
              ...settings,
              notesLabel: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Notas" data-id="element-2587" />
              </div>}

            <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-2588">
              <div data-id="element-2589">
                <p className="font-medium text-gray-900 dark:text-white" data-id="element-2590">
                  Pie de Página
                </p>
              </div>
              <input type="checkbox" checked={settings.showFooter} onChange={e => setSettings({
              ...settings,
              showFooter: e.target.checked
            })} className="w-5 h-5 rounded border-gray-300 text-black focus:ring-black dark:border-gray-600 dark:bg-gray-700" data-id="element-2591" />
            </label>

            {settings.showFooter && <div className="ml-4" data-id="element-2592">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-2593">
                  Texto del Pie de Página
                </label>
                <textarea value={settings.footerText} onChange={e => setSettings({
              ...settings,
              footerText: e.target.value
            })} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Documento generado por La Cochera Restaurant" data-id="element-2594" />
              </div>}
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex flex-col sm:flex-row justify-end gap-3" data-id="element-2595">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => {
          const defaultSettings = {
            companyName: 'La Cochera',
            companySubtitle: 'Restaurant',
            showLogo: true,
            showOrderDateTime: false,
            showDeliveryDateTime: false,
            showDeliveryType: false,
            showEmployeeCode: true,
            showRequests: false,
            showQuantity: true,
            showSignature: true,
            showNotes: true,
            notesLabel: 'Notas',
            showFooter: true,
            footerText: 'Documento generado por La Cochera Restaurant',
            primaryColor: '#000000',
            accentColor: '#3b82f6',
            fontFamily: 'Segoe UI',
            fontSize: '11pt',
            headerStyle: 'modern' as const,
            tableStyle: 'striped' as const,
            borderRadius: '8px',
            logoUrl: '',
            logoSize: '60px'
          };
          setSettings(defaultSettings);
          showToast('info', 'Valores restaurados', 'Se han restaurado los valores por defecto');
        }} data-id="element-2596">
            Restaurar Valores por Defecto
          </Button>
          <Button onClick={handleSave} leftIcon={<Save size={18} data-id="element-2598" />} size="lg" className="w-full sm:w-auto" data-id="element-2597">
            Guardar Configuración
          </Button>
        </div>
      </div>
    </div>;
}